import { ComponentProps, useEffect, useMemo, useState } from 'react';
import { Box, TextField, Icon, withStyles } from 'vopay';
import type { AccountUserRole } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Button, Card, Chip, DropdownMenu, MaterialSymbol, Skeleton, Table } from 'UI';
import vopayApi from '@/services/api.config';
import { useDataFetchIndicator } from '@/hooks';
import useAccountUsers from '@/hooks/useApi/useAccountUsers';
import useFetchUsersByRole from '@/hooks/useApi/useFetchUsersByRole';
import useFetchAccountUserRoles from '@/hooks/useApi/useFetchAccountUserRoles';
import useDeleteAccountUserRole from '@/hooks/useApi/useDeleteAccountUserRole';
import useFetchRoleUserCounts from '@/hooks/useApi/useFetchRoleUserCounts';
import useAssignUserRole from '@/hooks/useApi/useAssignUserRole';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import { usePermissions } from '@/providers/Permissions.provider';
import useSubAccountsEnabled from '@/hooks/useSubAccountsEnabled';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import useResetPassword from './useResetPassword';
import UserRolesContainer from './UserRoles/UserRolesContainer';
import AccountUserPermissionsDrawer from './UserPermissions/AccountUserPermissionsDrawer';
import CreateRoleDrawer from './UserRoles/CreateRoleDrawer';
import RoleDetailsDrawer from './UserRoles/RoleDetailsDrawer';
import EditRoleDrawer from './UserRoles/EditRoleDrawer';
import RolePermissionsViewDrawer from './UserRoles/RolePermissionsViewDrawer';
import RoleUsersViewDrawer from './UserRoles/RoleUsersViewDrawer';
import SelectRoleDialogue from './UserRoles/SelectRoleDialogue';
import CreateUserDrawer from './CreateUser/CreateUserDrawer';
import {
  propagateRoleDeleteToSubAccounts,
  type PropagateRoleDeleteResult,
} from './UserRoles/propagateRoleDeleteToSubAccounts';
// Reuse the dialog typography classes already used by the Edit drawer's
// confirmation dialogs so the Delete dialog matches them visually.
import dialogStyles from './UserRoles/EditRoleDrawer.module.scss';
import styles from './UserManagement.module.scss';

const cn = withStyles(styles);

type ActionsMenuItem = {
  id: string;
  label: string;
  // Derive the literal-union type directly from MaterialSymbol's prop so the
  // value matches the icon font's available glyphs (`tsc --build` is stricter
  // than `tsc --noEmit` and rejects a plain `string` here).
  icon: ComponentProps<typeof MaterialSymbol>['icon'];
  colorScheme?: 'default' | 'destructive';
  onSelect: () => void;
};

type UserManagementProps = {
  /**
   * When set, all reads/writes target this sub-account (passed as the bearer
   * to every role/user hook call). Undefined = the logged-in account, i.e. the
   * main User Management page.
   */
  bearer?: string;
};

// Self-contained User Management: owns all state, data, and cross-cutting
// handlers, and renders the search + role tiles + table together with every
// role/user drawer. The main page renders it with no bearer; the sub-account
// drawer renders it inside its SideDrawer scoped to a sub-account via `bearer`.
export default function UserManagement({ bearer }: UserManagementProps) {
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { resetPassword } = useResetPassword();
  const { userSettings } = useUserSettingsProvider();
  const { users, fetching: fetchingUsers, fetchUsers, deleteUser } = useAccountUsers();
  const [, setDeleting] = useDataFetchIndicator();
  const [searchTerm, setSearchTerm] = useState('');
  const { permissions } = usePermissions();
  const subAccountsEnabled = useSubAccountsEnabled();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList' });

  const { t: tDeleteUser } = useTranslation('translations', {
    keyPrefix: 'Users.DeleteUser.DeleteUserDialogue',
  });

  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [permissionsEmail, setPermissionsEmail] = useState<string>();
  const [isCreateRoleDrawerOpen, setIsCreateRoleDrawerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AccountUserRole | null>(null);
  const [editingRole, setEditingRole] = useState<AccountUserRole | null>(null);
  const [viewingPermissionsRole, setViewingPermissionsRole] = useState<AccountUserRole | null>(null);
  const [viewingUsersRole, setViewingUsersRole] = useState<AccountUserRole | null>(null);
  const [assignRoleUser, setAssignRoleUser] = useState<{ Username: string } | null>(null);
  // When the admin clicks "+ New Role" from inside the Manage Role(s) dialog,
  // we remember who they were editing so we can auto-assign the new role to
  // that user once the Create Role drawer finishes.
  const [pendingNewRoleAssignee, setPendingNewRoleAssignee] = useState<{ Username: string } | null>(null);
  const [isCreateUserDrawerOpen, setIsCreateUserDrawerOpen] = useState(false);

  // In-flight Create User session paused while the admin creates a new role.
  // Non-null = "Create User drawer should reopen with this state once the
  // Create Role drawer closes." Cleared when the admin actually closes the
  // Create User drawer (submits or cancels).
  const [pendingCreateUserState, setPendingCreateUserState] = useState<{
    emailAddress: string;
    selectedRoleIds: number[];
  } | null>(null);

  const {
    fetching: fetchingRoleUsers,
    users: roleUsers,
    fetchUsersByRole,
    reset: resetRoleUsers,
  } = useFetchUsersByRole();

  const { deleteUserRole } = useDeleteAccountUserRole();
  const { assignUserRole } = useAssignUserRole();
  const { counts: roleCounts, usersByRole, fetchRoleUserCounts } = useFetchRoleUserCounts();
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const { roles, fetching: fetchingRoles, fetchAccountUserRoles } = useFetchAccountUserRoles();

  // Pull the user list for whichever role is currently selected. Lifted up
  // here so Role Details (count), the Users sub-drawer (list), and the Delete
  // confirmation (count) all share one fetch.
  useEffect(() => {
    if (selectedRole) {
      fetchUsersByRole(selectedRole.RoleID, bearer).catch(() => {
        toast.neutral(t('errors.loadRoleUsers', { defaultValue: "Could not load the role's users" }));
      });
    } else {
      resetRoleUsers();
    }
  }, [selectedRole?.RoleID]);

  // Refresh per-role user counts whenever the role list changes.
  useEffect(() => {
    if (roles.length === 0) return;

    fetchRoleUserCounts(
      roles.map((r) => r.RoleID),
      bearer,
    ).catch(() => {
      toast.neutral(t('errors.loadRoleUserCounts', { defaultValue: 'Could not load role user counts' }));
    });
  }, [roles, bearer]);

  // When the Create Role drawer closes (success or cancel) and we're in the
  // middle of a Create User flow that diverted to + New Role, reopen the
  // Create User drawer so the admin returns to where they were.
  useEffect(() => {
    if (!isCreateRoleDrawerOpen && pendingCreateUserState && !isCreateUserDrawerOpen) {
      setIsCreateUserDrawerOpen(true);
    }
  }, [isCreateRoleDrawerOpen, pendingCreateUserState, isCreateUserDrawerOpen]);

  // Invert `usersByRole` (RoleID -> users[]) into `rolesByUsername`
  // (Username -> roles[]) for rendering the Role(s) column. Keys are
  // lowercased on both insert + lookup to defend against any case
  // discrepancy between the two endpoints that supply the data
  // (/account/users vs /account/user-roles/role) — usernames are emails and
  // should be case-insensitive.
  const rolesByUsername = useMemo(() => {
    const map: Record<string, AccountUserRole[]> = {};

    for (const role of roles) {
      const usersInRole = usersByRole[role.RoleID] ?? [];

      for (const u of usersInRole) {
        const key = u.Username.toLowerCase();
        if (!map[key]) map[key] = [];
        map[key].push(role);
      }
    }

    return map;
  }, [roles, usersByRole]);

  // Fetch users on mount.
  useEffect(() => {
    fetchUsers({ Timezone: userSettings.timezone }, bearer).catch(() => {
      toast.neutral(t('errors.loadUsers', { defaultValue: 'Could not load users' }));
    });
  }, [bearer]);

  // Fetch roles on mount, if the user can view roles.
  useEffect(() => {
    if (permissions.ViewUserRoles || permissions.ManageUserRoles) {
      fetchAccountUserRoles(bearer).catch(() => {
        toast.neutral(t('errors.loadRoles', { defaultValue: 'Could not load roles' }));
      });
    }
  }, [permissions.ViewUserRoles, permissions.ManageUserRoles, bearer]);

  const filteredUsers = useMemo(
    () => users.filter((user) => searchTerm === '' || user.Username.toLowerCase().includes(searchTerm.toLowerCase())),
    [users, searchTerm],
  );

  const handleResetPasswordDialog = async (userEmail: string) => {
    const result = await alertDialog.open(
      t('ResetPassword.title', { defaultValue: 'Send Password Reset Email?' }),
      t('ResetPassword.description', {
        defaultValue: 'This will send a secure password reset link to the user’s email address.',
      }),
      {
        abortLabel: t('ResetPassword.cancelButton', { defaultValue: 'Cancel' }),
        confirmLabel: t('ResetPassword.sendButton', { defaultValue: 'Send Reset Email' }),
      },
    );

    if (result === 'confirm') {
      try {
        await resetPassword({ Username: userEmail });
        toast.success(t('ResetPassword.success', { defaultValue: 'The link was sent successfully.' }));
      } catch {
        toast.neutral(t('ResetPassword.fail', { defaultValue: 'Unable to send the link.' }));
      }
    }
  };

  /**
   * TODO: switch to useConfirmDialog once it works inside modal drawers — the
   * ConfirmationDialogue it renders is a plain (non-Radix) Modal, so it's
   * inert when this component is inside the sub-account drawer's modal SideDrawer (ie
   * you can't interact with it because the SideDrawer's modal={true}).
   * alertDialog (Radix-based) layers correctly, so it's used as a stopgap.
   */
  const handleDeleteUserDialog = async (username: string) => {
    const result = await alertDialog.caution(
      tDeleteUser('confirmation.title', { defaultValue: 'Are you sure you want to delete the user?' }),
      tDeleteUser('confirmation.subTitle', { defaultValue: 'This action cannot be undone.' }),
      {
        abortLabel: tDeleteUser('confirmation.cancelLabel', { defaultValue: 'No' }),
        confirmLabel: tDeleteUser('confirmation.confirmLabel', { defaultValue: 'Yes' }),
      },
    );

    if (result !== 'confirm') return;

    setDeleting(true);

    try {
      await deleteUser({ EmailAddress: username }, bearer);
      toast.success(tDeleteUser('success.title', { defaultValue: 'Success! The user was successfully deleted.' }));

      fetchUsers({ Timezone: userSettings.timezone }, bearer).catch(() => {
        toast.neutral(t('errors.refreshUsers', { defaultValue: 'Could not refresh the user list' }));
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      alertDialog.error(
        tDeleteUser('error.title', { defaultValue: 'Error! The user was not deleted.' }),
        tDeleteUser('error.subTitle', { defaultValue: '{{errorMessage}}', errorMessage }),
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className={cn('searchArea')}>
        <TextField
          key="UserSearchKeyword"
          name="UserSearchKeyword"
          placeholder={t('search.placeholder', { defaultValue: 'Search user' })}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          variant="outlineShadow"
          elementLeft={
            <div style={{ marginLeft: '14px' }}>
              <Icon icon="search" width="17px" height="17px" color="none" />
            </div>
          }
        />

        {permissions.AddUsers && (
          <Button
            id="showHideAccountCredentials"
            onClick={() => setIsCreateUserDrawerOpen(true)}
            colorScheme="primary"
            size="small"
          >
            {t('actions.addUser', { defaultValue: 'Add User' })}
          </Button>
        )}
      </div>

      <UserRolesContainer
        onCreateRoleClick={() => setIsCreateRoleDrawerOpen(true)}
        onRoleClick={(role) => setSelectedRole(role)}
        roles={roles}
        roleCounts={roleCounts}
        fetching={fetchingRoles}
      />

      <Card
        style={{
          padding: '16px',
        }}
      >
        {fetchingUsers ? (
          <Skeleton height="320px" />
        ) : (
          <Table
            id="users-list"
            zebra
            columns={[
              {
                id: 'Username',
                header: t('table.email', { defaultValue: 'Email Address' }),
                align: 'left',
                field: 'Username',
              },
              {
                id: 'DateAdded',
                header: t('table.dateAdded', { defaultValue: 'Date Added' }),
                align: 'left',
                field: 'DateAdded',
              },
              {
                id: 'Roles',
                header: t('table.roles', { defaultValue: 'Role(s)' }),
                align: 'left',
                template: ({ data }) => {
                  const userRoles = rolesByUsername[data.Username.toLowerCase()] ?? [];
                  if (userRoles.length === 0) return <span>-</span>;

                  return (
                    <Box flx="row" gapSize="6px" wrap="wrap">
                      {userRoles.map((role) => (
                        <Chip key={role.RoleID} label={role.Name} />
                      ))}
                    </Box>
                  );
                },
                visible: roles.length > 0,
              },
              {
                id: 'view',
                header: '',
                align: 'right',
                template: ({ data }) =>
                  data.ActionsMenu.length === 0 ? null : (
                    <Box jc="fe" gap="sm">
                      <DropdownMenu>
                        <DropdownMenu.Trigger asChild>
                          <Button size="small" colorScheme="secondary" iconRight="keyboard_arrow_down">
                            {t('actions.actions', { defaultValue: 'Actions' })}
                          </Button>
                        </DropdownMenu.Trigger>

                        <DropdownMenu.Content align="end">
                          {data.ActionsMenu.map((item) => (
                            <DropdownMenu.Item
                              key={item.id}
                              onSelect={() => item.onSelect()}
                              colorScheme={item.colorScheme}
                              left={
                                <MaterialSymbol
                                  icon={item.icon}
                                  size={18}
                                  color={item.colorScheme === 'destructive' ? '#e8160b' : undefined}
                                />
                              }
                            >
                              {item.label}
                            </DropdownMenu.Item>
                          ))}
                        </DropdownMenu.Content>
                      </DropdownMenu>
                    </Box>
                  ),
              },
            ]}
            rows={filteredUsers.map((user) => {
              const items: ActionsMenuItem[] = [];

              if (permissions.ManageUserPermissions) {
                items.push({
                  id: `user-select-role-${user.DateAdded}`,
                  label: t('actions.selectRole', { defaultValue: 'Manage Role(s)' }),
                  icon: 'groups_2',
                  onSelect: () => {
                    setAssignRoleUser({ Username: user.Username });
                  },
                });

                items.push({
                  id: `user-change-Permissions-${user.DateAdded}`,
                  label: t('actions.permissions', { defaultValue: 'Permissions' }),
                  icon: 'settings',
                  onSelect: () => {
                    setPermissionsEmail(user.Username);
                    setIsPermissionsDrawerOpen(true);
                  },
                });
              }

              if (permissions.AddUsers) {
                items.push({
                  id: `user-change-${user.DateAdded}`,
                  label: t('actions.resetPassword', { defaultValue: 'Reset Password' }),
                  icon: 'refresh',
                  onSelect: () => {
                    handleResetPasswordDialog(user.Username);
                  },
                });

                items.push({
                  id: `user-delete-${user.DateAdded}`,
                  label: t('actions.remove', { defaultValue: 'Remove' }),
                  icon: 'close',
                  colorScheme: 'destructive',
                  onSelect: () => {
                    handleDeleteUserDialog(user.Username);
                  },
                });
              }

              return {
                id: user.Username,
                data: {
                  Username: user.Username,
                  DateAdded: user.DateAdded,
                  Roles: '-',
                  ActionsMenu: items,
                },
              };
            })}
          />
        )}
      </Card>

      <AccountUserPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        emailAddress={permissionsEmail}
        availableRoles={roles}
        accountToken={bearer}
      />

      <CreateUserDrawer
        isOpen={isCreateUserDrawerOpen}
        bearer={bearer}
        existingUsernames={users.map((u) => u.Username)}
        availableRoles={roles}
        initialEmailAddress={pendingCreateUserState?.emailAddress}
        initialRoleIds={pendingCreateUserState?.selectedRoleIds}
        onClose={() => {
          setIsCreateUserDrawerOpen(false);
          // Drawer closing for real (submit or cancel) — discard any paused
          // session state so the next Add User click starts fresh.
          setPendingCreateUserState(null);
        }}
        onUserCreated={() => {
          fetchUsers({ Timezone: userSettings.timezone }, bearer).catch(() => {
            toast.neutral(t('errors.refreshUsers', { defaultValue: 'Could not refresh the user list' }));
          });

          // Roles also need refresh in case the new user was given any roles
          // during creation (affects Role(s) column). Tile counts refresh
          // automatically via the [roles, bearer] effect once roles updates.
          fetchAccountUserRoles(bearer).catch(() => {
            toast.neutral(t('errors.refreshRoles', { defaultValue: 'Could not refresh roles' }));
          });
        }}
        onCreateNewRole={
          // Only show "+ New Role" for admins who can actually create role
          // definitions. We pause the Create User session (saving email +
          // role selections) and divert to Create Role. After Create Role
          // closes (success or cancel) the effect above reopens Create User
          // with the saved state.
          permissions.ManageUserRoles
            ? (currentState) => {
                setPendingCreateUserState(currentState);
                // Bypass onClose handler — we don't want to clear the paused
                // state here; that only happens when the drawer truly closes.
                setIsCreateUserDrawerOpen(false);
                setIsCreateRoleDrawerOpen(true);
              }
            : undefined
        }
      />

      <CreateRoleDrawer
        isOpen={isCreateRoleDrawerOpen}
        bearer={bearer}
        onClose={() => {
          setIsCreateRoleDrawerOpen(false);
          // Clear the pending assignee when the drawer closes (covers Cancel
          // path). Setting it again on open if needed is handled by
          // onCreateNewRole above.
          setPendingNewRoleAssignee(null);
        }}
        suppressSuccessToast={!!pendingNewRoleAssignee}
        onRoleCreated={async (newRole) => {
          // Always refresh the roles list.
          const fetchingAccountUserRoles = fetchAccountUserRoles(bearer).catch(() => {
            toast.neutral(t('errors.refreshRoles', { defaultValue: 'Could not refresh roles' }));
          });

          // Came from the Create User drawer's "+ New Role" chain — append
          // the new role to the paused session so it's pre-selected when
          // Create User reopens.
          if (pendingCreateUserState) {
            setPendingCreateUserState({
              emailAddress: pendingCreateUserState.emailAddress,
              selectedRoleIds: [...pendingCreateUserState.selectedRoleIds, newRole.RoleID],
            });

            await fetchingAccountUserRoles;

            return;
          }

          // Auto-assign the new role to the user we were editing, when this
          // flow originated from the Manage Role(s) dialog.
          if (pendingNewRoleAssignee) {
            const assignee = pendingNewRoleAssignee;
            setPendingNewRoleAssignee(null);

            try {
              await assignUserRole({ Username: assignee.Username, RoleID: newRole.RoleID }, bearer);

              toast.success(
                t('actions.roleCreatedAndAssigned', {
                  defaultValue: 'Role "{{roleName}}" was created and assigned to {{username}}.',
                  roleName: newRole.Name,
                  username: assignee.Username,
                }),
              );

              // Refresh role->user mapping so the Role(s) column + tile count
              // both reflect the new assignment immediately.
              await Promise.all([
                fetchingAccountUserRoles,
                fetchRoleUserCounts([...roles.map((r) => r.RoleID), newRole.RoleID], bearer).catch(() => {
                  toast.neutral(
                    t('errors.refreshRoleUserCounts', { defaultValue: 'Could not refresh role user counts' }),
                  );
                }),
              ]);
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Unknown error';

              // Error severity (not neutral): the role exists but the expected
              // chained assignment didn't land — admin needs to notice, since
              // recovery is manual via Manage Role(s).
              alertDialog.error(
                t('actions.roleAssignFailedTitle', { defaultValue: 'Role created but not assigned' }),
                t('actions.roleAssignFailed', {
                  defaultValue:
                    'Role "{{roleName}}" was created, but could not be assigned to {{username}}: {{errorMessage}}\n\nYou can assign it manually from Actions → Manage Role(s).',
                  roleName: newRole.Name,
                  username: assignee.Username,
                  errorMessage,
                }),
              );
            }
          }
        }}
      />

      <RoleDetailsDrawer
        isOpen={!!selectedRole && !editingRole}
        role={selectedRole}
        usersCount={roleUsers.length}
        fetchingUsers={fetchingRoleUsers}
        subAccountsEnabled={subAccountsEnabled}
        canManage={!!permissions.ManageUserRoles}
        onClose={() => setSelectedRole(null)}
        onEdit={() => {
          if (selectedRole) setEditingRole(selectedRole);
        }}
        onDelete={async () => {
          if (!selectedRole) return;
          const roleToDelete = selectedRole;
          const usersAffected = roleUsers.length;

          // When the role propagates to sub-accounts, pre-fetch the sub-account
          // count so the dialog can show "Removed from <y> sub-accounts".
          let subAccountsCount = 0;
          const propagatesToSubAccounts = roleToDelete.ApplyToAllSubaccounts && subAccountsEnabled;

          if (propagatesToSubAccounts) {
            try {
              const res = await fetchSubAccountsAsync({ ShowToken: true, ActiveOnly: true }, bearer);
              subAccountsCount = res.Success ? (res.Subaccounts?.length ?? 0) : 0;
            } catch (err) {
              console.error('Failed to fetch sub-accounts for delete dialog:', err);
            }
          }

          const confirmation = await alertDialog.caution(
            t('deleteRoleTitle', { defaultValue: 'Delete Role?' }),
            propagatesToSubAccounts ? (
              <div data-apply-confirm-dialog="delete-role-cascade">
                <p className={dialogStyles.confirmDialogLead}>
                  {t('deleteRoleCascadeLead', {
                    defaultValue: 'Are you sure you want to delete this role? This role will be:',
                  })}
                </p>

                <ul className={dialogStyles.confirmDialogList}>
                  <li>
                    {t('deleteRoleCascadeBullet1', {
                      defaultValue: 'Removed from {{numSubAccounts}} sub-accounts',
                      numSubAccounts: subAccountsCount,
                    })}
                  </li>

                  <li>
                    {t('deleteRoleCascadeBullet2', {
                      defaultValue: 'Unassigned from {{numUsers}} users',
                      numUsers: usersAffected,
                    })}
                  </li>
                </ul>
              </div>
            ) : (
              <div data-apply-confirm-dialog="delete-role">
                <p className={dialogStyles.confirmDialogLead}>
                  {t('deleteRoleBody', {
                    defaultValue:
                      'Are you sure you want to delete this role? This role will be unassigned from {{count}} users.',
                    count: usersAffected,
                  })}
                </p>
              </div>
            ),
            {
              abortLabel: t('goBack', { defaultValue: 'Go Back' }),
              confirmLabel: tGlobal('delete'),
            },
          );

          if (confirmation !== 'confirm') return;

          try {
            setDeleting(true);
            await deleteUserRole({ RoleID: roleToDelete.RoleID }, bearer);

            // Fan out the delete to sub-accounts if the role was propagated.
            // Each sub-account has its own RoleID, looked up by Name.
            let subResult: PropagateRoleDeleteResult | null = null;

            // _TODO: consider folding this directly into deleteUserRole
            if (roleToDelete.ApplyToAllSubaccounts && subAccountsEnabled) {
              subResult = await propagateRoleDeleteToSubAccounts(roleToDelete.Name, {
                // In sub-account context the bearer scopes enumeration to the
                // sub-account's own children.
                fetchSubAccountsAsync: (params) => fetchSubAccountsAsync(params, bearer),
                fetchAccountUserRolesWithBearer: (childBearer) =>
                  // _TODO: don't call endpoints directly.
                  vopayApi.api.fetchAccountUserRoles(childBearer),
                deleteUserRole,
              });
            }

            // Close drawers, refresh, and toast.
            setSelectedRole(null);
            setEditingRole(null);
            setViewingPermissionsRole(null);
            setViewingUsersRole(null);

            fetchAccountUserRoles(bearer).catch(() => {
              toast.neutral(t('errors.refreshRoles', { defaultValue: 'Could not refresh roles' }));
            });

            if (subResult && (subResult.failed.length > 0 || subResult.skipped.length > 0)) {
              toast.neutral(
                t('deleteRolePartial', {
                  defaultValue:
                    'Role "{{roleName}}" deleted. Removed from {{numDeleted}} of {{numTotal}} sub-accounts ({{numFailed}} failed, {{numSkipped}} skipped).',
                  roleName: roleToDelete.Name,
                  numDeleted: subResult.numDeleted,
                  numTotal: subResult.numTotal,
                  numFailed: subResult.failed.length,
                  numSkipped: subResult.skipped.length,
                }),
              );
            } else {
              toast.success(
                t('deleteRoleSuccess', {
                  defaultValue: 'Role "{{roleName}}" was deleted successfully.',
                  roleName: roleToDelete.Name,
                }),
              );
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';

            alertDialog.error(
              t('deleteRoleErrorTitle', { defaultValue: 'Could not delete role' }),
              t('deleteRoleErrorBody', { defaultValue: '{{errorMessage}}', errorMessage }),
            );
          } finally {
            setDeleting(false);
          }
        }}
        onViewPermissions={() => {
          if (selectedRole) setViewingPermissionsRole(selectedRole);
        }}
        onViewUsers={() => {
          if (selectedRole) setViewingUsersRole(selectedRole);
        }}
      />

      <RolePermissionsViewDrawer
        isOpen={!!viewingPermissionsRole}
        role={viewingPermissionsRole}
        onClose={() => setViewingPermissionsRole(null)}
      />

      <RoleUsersViewDrawer
        isOpen={!!viewingUsersRole}
        role={viewingUsersRole}
        users={roleUsers}
        fetching={fetchingRoleUsers}
        onClose={() => setViewingUsersRole(null)}
      />

      <SelectRoleDialogue
        isOpen={!!assignRoleUser}
        user={assignRoleUser}
        bearer={bearer}
        availableRoles={roles}
        onClose={() => setAssignRoleUser(null)}
        onAssignmentsChanged={() => {
          // Refresh tile counts so the count for any newly-assigned role bumps.
          if (roles.length > 0) {
            fetchRoleUserCounts(
              roles.map((r) => r.RoleID),
              bearer,
            ).catch(() => {
              toast.neutral(t('errors.refreshRoleUserCounts', { defaultValue: 'Could not refresh role user counts' }));
            });
          }
        }}
        onCreateNewRole={
          // The "+ New Role" footer button only makes sense for admins who can
          // actually create a role definition (ManageUserRoles). Without the
          // callback, SelectRoleDialogue hides the footer entirely.
          permissions.ManageUserRoles
            ? () => {
                // Remember the user so we can auto-assign the new role to them
                // after the Create Role drawer succeeds.
                setPendingNewRoleAssignee(assignRoleUser);
                setAssignRoleUser(null);
                setIsCreateRoleDrawerOpen(true);
              }
            : undefined
        }
      />

      <EditRoleDrawer
        isOpen={!!editingRole}
        role={editingRole}
        bearer={bearer}
        onClose={() => {
          setEditingRole(null);
          setSelectedRole(null);
        }}
        onRoleUpdated={() => {
          setSelectedRole(null);

          fetchAccountUserRoles(bearer).catch(() => {
            toast.neutral(t('errors.refreshRoles', { defaultValue: 'Could not refresh roles' }));
          });
        }}
      />
    </div>
  );
}
